import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "./fm";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Mail,
  Sparkles,
  TriangleAlert,
  Wifi,
  WifiOff,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "./utils/cn";

const EASE = [0.22, 1, 0.36, 1];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const DISSOLVE_MS = 650;
const DISSOLVE = { duration: DISSOLVE_MS / 1000, ease: EASE };

function Field({
  label,
  type,
  value,
  onChange,
  icon,
  error,
  hint,
  autoComplete,
  trailing,
  onKey,
  valid,
  inputRef,
  onEnterSubmit,
}) {
  const [focused, setFocused] = useState(false);
  const lifted = focused || value.length > 0;
  return (
      <div>
      <div
        className={cn(
          "group relative rounded-2xl border-2 bg-white transition-all duration-300",
          error
            ? "border-red-400 shadow-[0_0_0_4px_rgba(248,113,113,0.12)]"
            : focused
              ? "border-navy-900 shadow-[0_0_0_4px_rgba(255,210,31,0.45)]"
              : "border-slate-200 hover:border-slate-300"
        )}
      >
        <span
          className={cn(
            "pointer-events-none absolute top-1/2 left-11 z-10 -translate-y-1/2 bg-transparent px-1 font-sans text-slate-400 transition-all duration-200",
            lifted
              ? "top-0 z-10 bg-white text-[10px] font-semibold tracking-[0.14em] uppercase"
              : "text-[15px]",
            lifted && error
              ? "text-red-500"
              : lifted && focused
                ? "text-navy-900"
                : "text-slate-400"
          )}
        >
          {label}
        </span>
        <span
          className={cn(
            "pointer-events-none absolute top-1/2 left-4 z-10 -translate-y-1/2 transition-colors duration-200",
            error ? "text-red-400" : focused ? "text-azure-500" : "text-slate-400"
          )}
        >
          {icon}
        </span>
        <input
          ref={inputRef}
          type={type}
          value={value}
          aria-label={label}
          autoComplete={autoComplete}
          spellCheck={false}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              if (onEnterSubmit) {
                e.preventDefault();
                onEnterSubmit();
              }
            }
            if (onKey) onKey(e);
          }}
          onKeyUp={(e) => {
            if (e.key === "Enter") return;
            if (onKey) onKey(e);
          }}
          className="relative z-20 w-full bg-transparent px-11 py-4 text-[15px] font-medium text-navy-950 outline-none placeholder:text-transparent"
          placeholder={label}
        />
        <div className="pointer-events-none absolute top-1/2 right-4 z-30 flex -translate-y-1/2 items-center gap-2">
          {trailing}
        </div>
        <AnimatePresence>
          {valid && !error && value.length > 0 && (
            <motion.span
              key="valid-check"
              initial={{ scale: 0, rotate: -90 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0 }}
              className="absolute top-0 right-3 z-10 grid h-5 w-5 -translate-y-1/2 place-items-center rounded-full bg-emerald-500 text-white"
            >
              <Check className="h-3 w-3" strokeWidth={3} />
            </motion.span>
          )}
        </AnimatePresence>
      </div>
      <AnimatePresence initial={false}>
        {(error || hint) && (
          <motion.div
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: "auto", marginTop: 6 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            transition={{ duration: 0.25, ease: EASE }}
            className="overflow-hidden"
          >
            {error ? (
              <p className="flex items-center gap-1.5 text-xs font-medium text-red-500">
                <TriangleAlert className="h-3.5 w-3.5" /> {error}
              </p>
            ) : (
              <div className="flex items-center gap-1.5 text-xs font-medium text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5" /> {hint}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const AUTH_STEPS = [
  "Verifying credentials",
  "Evaluating device posture",
  "Applying security policy",
  "Provisioning session",
];

function Authenticating({ progressStep }) {
  return (
    <div className="py-10">
      <div className="mx-auto mb-8 grid h-14 w-14 place-items-center rounded-2xl bg-navy-950 shadow-[0_16px_40px_-12px_rgba(7,16,39,0.55)]">
        <Loader2 className="h-6 w-6 animate-spin text-sun-400" />
      </div>
      <div className="space-y-3">
        {AUTH_STEPS.map((s, i) => {
          const state =
            i < progressStep ? "done" : i === progressStep ? "active" : "idle";
          return (
            <motion.div
              key={s}
              initial={{ opacity: 0, y: 10 }}
              animate={{
                opacity: state === "idle" ? 0.35 : 1,
                y: 0,
              }}
              transition={{ delay: i * 0.06, duration: 0.4, ease: EASE }}
              className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3"
            >
              <span
                className={cn(
                  "grid h-5 w-5 place-items-center rounded-full transition-colors duration-300",
                  state === "done"
                    ? "bg-emerald-500 text-white"
                    : state === "active"
                      ? "bg-sun-400 text-navy-950"
                      : "bg-slate-100 text-slate-400"
                )}
              >
                {state === "done" ? (
                  <Check className="h-3 w-3" strokeWidth={3.5} />
                ) : state === "active" ? (
                  <Loader2 className="h-3 w-3 animate-spin" strokeWidth={3.5} />
                ) : (
                  <span className="h-1 w-1 rounded-full bg-slate-300" />
                )}
              </span>
              <span
                className={cn(
                  "text-sm font-medium",
                  state === "idle" ? "text-slate-400" : "text-navy-950"
                )}
              >
                {s}
              </span>
              {state === "active" && (
                <span className="ml-auto font-mono text-[10px] tracking-[0.18em] text-slate-400">
                  RUNNING
                  <span className="animate-caret">_</span>
                </span>
              )}
            </motion.div>
          );
        })}
      </div>
      <p className="mt-6 text-center font-mono text-[10px] tracking-[0.24em] uppercase text-slate-400">
        Encrypted channel · TLS 1.3
      </p>
    </div>
  );
}

export default memo(function LoginForm({
  onDirtyChange,
  onLogin,
  axiosInstance,
  backendStatus,
  onBackendStatusChange,
  onShowForgotPassword,
}) {
  const [step, setStep] = useState("form");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [trust, setTrust] = useState(true);
  const [capsLock, setCapsLock] = useState(false);
  const [touched, setTouched] = useState({});
  const [progress, setProgress] = useState(0);
  const [serverError, setServerError] = useState("");
  const [authProgressStep, setAuthProgressStep] = useState(0);
  const [welcomeName, setWelcomeName] = useState("");
  const completedLoginRef = useRef(null);
  const [renderStep, setRenderStep] = useState("form");
  const [phase, setPhase] = useState("enter");
  const stepSwitchTimerRef = useRef(null);
  const pwRef = useRef(null);
  const formRef = useRef(null);
  const submittingRef = useRef(false);
  const submitCountRef = useRef(0);
  const authProgressTimer = useRef(null);

  const writeDebugLog = (event, payload = {}) => {
    const entry = {
      t: Date.now(),
      event,
      payload,
      stack: new Error().stack?.split("\n").slice(2, 6).join(" | ") || null,
    };
    try {
      const list = JSON.parse(localStorage.getItem("debug_login_log") || "[]");
      list.push(entry);
      localStorage.setItem("debug_login_log", JSON.stringify(list.slice(-60)));
    } catch (_) {
      /* ignore */
    }
    window.dispatchEvent(new CustomEvent("debug-login", { detail: entry }));
  };

  // Restore remembered credentials on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem("rememberedCredentials");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.remember) {
          if (typeof parsed.email === "string") setEmail(parsed.email);
          if (typeof parsed.password === "string") setPassword(parsed.password);
          if (typeof parsed.remember === "boolean") setTrust(parsed.remember);
        }
      }
    } catch (_) {
      /* ignore */
    }
  }, []);

  const emailValid = EMAIL_RE.test(email);
  const pwValid = password.length >= 6;
  if (touched.email || submitCountRef.current > 0) {
    writeDebugLog("validate.email", {
      email,
      emailValid,
      regexString: EMAIL_RE.source,
    });
  }
  const emailErr =
    touched.email && email.length > 0 && !emailValid
      ? "Enter a valid corporate email address"
      : "";
  const pwErr =
    touched.pw && password.length > 0 && !pwValid
      ? "Password must be at least 6 characters"
      : "";

  const friendlyName = useMemo(() => {
    const local = email.split("@")[0] || "user";
    const parts = local.split(/[.\-_+\d]+/).filter(Boolean);
    const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
    return parts.length
      ? parts.map(cap).slice(0, 2).join(" ")
      : cap(local);
  }, [email]);

  useEffect(() => {
    onDirtyChange?.(
      step !== "form" || email.length > 0 || password.length > 0
    );
  }, [step, email, password, onDirtyChange]);

  useEffect(() => {
    if (stepSwitchTimerRef.current) {
      clearTimeout(stepSwitchTimerRef.current);
      stepSwitchTimerRef.current = null;
    }
    if (step === renderStep && phase === "enter") {
      return;
    }
    if (step !== renderStep) {
      setPhase("leave");
      stepSwitchTimerRef.current = setTimeout(() => {
        setRenderStep(step);
        setPhase("enter");
      }, DISSOLVE_MS);
    }
    return () => {
      if (stepSwitchTimerRef.current) {
        clearTimeout(stepSwitchTimerRef.current);
      }
    };
  }, [step, renderStep, phase]);

  const advanceAuthSteps = useCallback(() => {
    setAuthProgressStep(0);
    if (authProgressTimer.current) clearInterval(authProgressTimer.current);
    let i = 0;
    authProgressTimer.current = setInterval(() => {
      i += 1;
      setAuthProgressStep(i);
      if (i >= AUTH_STEPS.length) {
        clearInterval(authProgressTimer.current);
      }
    }, 560);
  }, []);

  useEffect(() => {
    return () => {
      if (authProgressTimer.current) clearInterval(authProgressTimer.current);
      if (stepSwitchTimerRef.current) clearTimeout(stepSwitchTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (step !== "success") return;
    const start = Date.now();
    const t = setInterval(() => {
      const p = Math.min(1, (Date.now() - start) / 2200);
      setProgress(Math.round(p * 100));
      if (p >= 1) {
        clearInterval(t);
        const stored = completedLoginRef.current;
        if (stored && onLogin) {
          setTimeout(() => {
            onLogin(stored.user, stored.access_token);
          }, 450);
        }
      }
    }, 40);
    return () => clearInterval(t);
  }, [step, onLogin]);

  const doSubmit = useCallback(async () => {
    submitCountRef.current += 1;
    writeDebugLog("submit.doSubmit.entry", {
      submittingRef: submittingRef.current,
      submitCount: submitCountRef.current,
      email,
      emailValid,
      pwValid,
      passwordLen: password.length,
    });
    if (submittingRef.current) {
      writeDebugLog("submit.doSubmit.DROPPED_alreadySubmitting", {
        submitCount: submitCountRef.current,
      });
      return;
    }
    submittingRef.current = true;
    setTouched({ email: true, pw: true });
    setServerError("");
    if (!emailValid || !pwValid) {
      writeDebugLog("submit.doSubmit.DROPPED_invalid", { emailValid, pwValid });
      submittingRef.current = false;
      return;
    }

    const base = axiosInstance?.defaults?.baseURL || "unknown";
    if (
      typeof window !== "undefined" &&
      window.location.protocol === "https:" &&
      String(base).startsWith("http://")
    ) {
      toast.error(
        "Mixed content: backend is http while page is https. Set REACT_APP_BACKEND_URL to an https backend."
      );
      submittingRef.current = false;
      return;
    }

    setStep("authenticating");
    advanceAuthSteps();

    try {
      writeDebugLog("submit.axios.post", { email: email.trim() });
      const response = await axiosInstance.post("/auth/login", {
        email: email.trim(),
        password,
      });
      writeDebugLog("submit.axios.response", {
        status: response.status,
        hasToken: Boolean(response.data?.access_token),
        hasUser: Boolean(response.data?.user),
        user_full_name: response.data?.user?.full_name ?? null,
      });
      // Any successful /auth/login response means origin is reachable and the
      // backend process is alive.  Flip the connectivity chip back to the
      // green "Connected" state if it was previously red after a 502.
      onBackendStatusChange?.({ ok: true, message: "Connected" });
      const { access_token, user } = response.data;

      if (trust) {
        try {
          localStorage.setItem(
            "rememberedCredentials",
            JSON.stringify({
              email: email.trim(),
              password,
              remember: true,
            })
          );
        } catch (_) {
          /* ignore */
        }
      } else {
        try {
          localStorage.removeItem("rememberedCredentials");
        } catch (_) {
          /* ignore */
        }
      }

      if (user && user.full_name) {
        setWelcomeName(user.full_name);
      } else {
        setWelcomeName(friendlyName);
      }

      const finalStepDelay = Math.max(
        0,
        750 + 560 * (AUTH_STEPS.length - 1) + 420 - 560 * authProgressStep
      );
      writeDebugLog("submit.schedulingStepSuccess", { finalStepDelay });
      setTimeout(() => {
        setStep("success");
        completedLoginRef.current = { user, access_token };
        toast.success(`Welcome back, ${user.full_name || welcomeName}!`);
        submittingRef.current = false;
        writeDebugLog("submit.stepSuccess", { submitCount: submitCountRef.current });
      }, finalStepDelay);
    } catch (error) {
      const status = error?.response?.status;
      const ctRaw = error?.response?.headers?.["content-type"] ??
                   error?.response?.headers?.["Content-Type"] ??
                   null;
      const ct = typeof ctRaw === "string" ? ctRaw.toLowerCase() : "";
      const isJson = ct.includes("application/json");
      const body = error?.response?.data;
      const bodyByteSize =
        body == null
          ? 0
          : typeof body === "string"
            ? body.length
            : typeof body === "object"
              ? JSON.stringify(body).length
              : 0;

      // ---------------------------------------------------------------------
      // Error severity classification — determines how it surfaces to both
      // the developer console AND the end user:
      //
      //   "backend-down"   → Origin is not serving HTTP.  Transport layer
      //                      failure (no TCP ACK), or reverse proxy returned
      //                      a gateway error (502/503/504/52x) with an
      //                      HTML/plain-text body (Cloudflare, Emergent,
      //                      Nginx).  This is a routine "service not yet
      //                      booted / restarting" event, NOT an application
      //                      bug.  Log to console.info so DevTools does NOT
      //                      paint it bright red on the user's Console tab.
      //
      //   "app-error-json" → FastAPI returned a structured JSON error
      //                      (HTTP 400/401/403/422/500).  Our tolerant
      //                      backend catch-all always emits JSON.  Treat
      //                      as an application-level event; 401 (wrong pw)
      //                      is also info-only, not a "red console error".
      //                      Only 5xx-JSON go through console.error since
      //                      they indicate server bugs worth filing.
      //
      //   "unknown"        → Anything else; always console.error so we
      //                      catch new edge cases introduced by future
      //                      edits.
      // ---------------------------------------------------------------------
      let errorClass = "unknown";
      const isBackendDown =
        status == null ||
        status === 502 ||
        status === 503 ||
        status === 504 ||
        (status >= 520 && status <= 530);

      if (isBackendDown) {
        errorClass = "backend-down";
      } else if (isJson) {
        // 4xx JSON = app validation / auth events.  500 JSON = server bug.
        // Both share the "app-error-json" class name; the status code itself
        // is used downstream to pick console.info vs console.error severity.
        errorClass = "app-error-json";
      }

      writeDebugLog("submit.CATCH", {
        status: status ?? null,
        content_type: ct || null,
        body_bytes: bodyByteSize,
        is_json: isJson,
        json_detail: isJson && body ? body?.detail ?? null : null,
        error_class: errorClass,
        code: error?.code ?? null,
        name: error?.name ?? null,
      });

      // Severity-aware console logging.  BACKEND-DOWN events must NEVER
      // paint the DevTools console red — they are normal during restart
      // / first-boot windows and were causing the user to file spurious
      // bug reports about "sign-in page errors" every time the backend
      // process recycled.
      if (errorClass === "backend-down") {
        console.info(
          `[sign-in:${errorClass}] backend unavailable (status=${
            status == null ? "transport-failure" : status
          }, ct=${ct || "n/a"}, bytes=${bodyByteSize}). Not an app bug — backend is restarting or not listening.`,
        );
      } else if (errorClass === "app-error-json" && status !== 500) {
        // 4xx JSON responses are expected validation / auth results.
        console.info(
          `[sign-in:app-validation] status=${status} detail=${
            body?.detail ?? "n/a"
          }`,
        );
      } else {
        // Anything else (JSON 500, unknown response type, unexpected
        // throw) → keep as console.error so developers see bright red and
        // investigate.
        console.error("Login error:", error);
      }

      // ---------------------------------------------------------------------
      // User-facing message selection.  KEY INVARIANT: never, under any
      // circumstances, dump raw reverse-proxy HTML banner text into the
      // sign-in failed red card or the toast.  Cloudflare / Emergent /
      // Nginx HTML banners contain scary language ("misconfigured origin")
      // that end users cannot act on — they just need a clean "try again
      // in a moment" message.
      // ---------------------------------------------------------------------
      let userDetail = "Login failed. Please try again.";
      let serverCardMsg = userDetail;

      if (isBackendDown) {
        userDetail =
          status === 502
            ? "Backend servers are not responding right now (502 Bad Gateway). Please try again in 30 seconds."
            : status === 503
              ? "Backend is temporarily down for maintenance (503). Please try again shortly."
              : status === 504
                ? "Backend servers timed out (504). Please try again."
                : status == null
                  ? "Cannot reach the backend servers right now. Please check your connection or try again."
                  : `Backend returned an error (HTTP ${status}). Please try again.`;
        serverCardMsg = userDetail;
      } else if (isJson) {
        // FastAPI / our own backend responded with a JSON body.  Use
        // `detail` if present, otherwise fall back to per-status defaults.
        const jsonDetail = body?.detail;
        if (status === 401) {
          // 401 unauthorized → ALWAYS "wrong email or password" regardless
          // of server detail, to prevent account enumeration.
          serverCardMsg = "Incorrect email or password";
          userDetail = serverCardMsg;
        } else if (status === 403) {
          serverCardMsg = jsonDetail || "Your account has been deactivated. Contact the administrator.";
          userDetail = serverCardMsg;
        } else if (status === 500) {
          serverCardMsg = jsonDetail || "Server error. Contact the administrator.";
          userDetail = serverCardMsg;
        } else if (typeof jsonDetail === "string" && jsonDetail.length > 0) {
          serverCardMsg = jsonDetail;
          userDetail = jsonDetail;
        } else if (status === 422) {
          serverCardMsg = "Invalid input. Check your email format and password length.";
          userDetail = serverCardMsg;
        } else {
          serverCardMsg = `Sign in failed (HTTP ${status}).`;
          userDetail = jsonDetail || serverCardMsg;
        }
      } else {
        // Non-JSON, non-gateway response (unlikely, but defensive) → do
        // not render raw bytes.  Show a friendly generic fallback.
        serverCardMsg = "Sign in failed. Please try again.";
        userDetail = serverCardMsg;
      }

      if (isBackendDown) {
        const downMsg =
          status === 502
            ? "Origin unreachable (502 Bad Gateway)"
            : status === 503
              ? "Service unavailable (503)"
              : status === 504
                ? "Origin timed out (504 Gateway Timeout)"
                : status == null
                  ? "Backend unreachable"
                  : `Origin error (HTTP ${status})`;
        onBackendStatusChange?.({ ok: false, message: downMsg });
        // Still show a red sign-in failed card so the first submit explains
        // what happened (the button then disables for subsequent clicks).
        setServerError(serverCardMsg);
      } else {
        setServerError(serverCardMsg);
      }

      toast.error(userDetail);
      if (authProgressTimer.current) clearInterval(authProgressTimer.current);
      setTimeout(() => {
        setStep("form");
        submittingRef.current = false;
      }, 900);
    } finally { /* noop */ }
  }, [email, password, emailValid, pwValid, axiosInstance, trust, advanceAuthSteps, authProgressStep, onLogin, onBackendStatusChange]);

  const handleSubmit = async (e) => {
    writeDebugLog("submit.handleSubmit.entry", {
      hasEvent: Boolean(e),
      submitCount: submitCountRef.current,
      submittingRef: submittingRef.current,
    });
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    await doSubmit();
  };

  const reset = () => {
    if (stepSwitchTimerRef.current) clearTimeout(stepSwitchTimerRef.current);
    setStep("form");
    setRenderStep("form");
    setPhase("enter");
    setPassword("");
    setTouched({});
    setProgress(0);
    setServerError("");
    setWelcomeName("");
    completedLoginRef.current = null;
  };

  const detectCaps = (e) => {
    const caps = e.getModifierState?.("CapsLock");
    if (typeof caps === "boolean") setCapsLock(caps);
  };

  const backendOk = backendStatus?.ok;
  const backendMsg = backendStatus?.message || "";

  return (
    <div className="relative flex w-full flex-col">
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: EASE }}
        className="flex w-full items-center justify-between"
      >
        <span
          title={backendMsg || "Backend status"}
          className={cn(
            "flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-[10px] tracking-[0.16em]",
            backendOk === true
              ? "border-emerald-300/40 bg-emerald-50 text-emerald-700"
              : backendOk === false
                ? "border-red-300/40 bg-red-50 text-red-700"
                : "border-slate-200 bg-white/80 text-slate-500"
          )}
        >
          {backendOk === true ? (
            <Wifi className="h-3 w-3 text-emerald-500" />
          ) : backendOk === false ? (
            <WifiOff className="h-3 w-3 text-red-500" />
          ) : (
            <Loader2 className="h-3 w-3 animate-spin" />
          )}
          {backendMsg || "Checking backend"}
        </span>
        <span className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white/80 px-3 py-1 font-mono text-[10px] tracking-[0.16em] text-slate-500">
          <Sparkles className="h-3 w-3 text-sun-500" /> EN
        </span>
      </motion.div>

      {renderStep === "form" && (
        <motion.div
          initial={false}
          animate={
            phase === "enter"
              ? { opacity: 1, filter: "blur(0px)", scale: 1 }
              : { opacity: 0, filter: "blur(10px)", scale: 0.98 }
          }
          transition={DISSOLVE}
          className="mx-auto mt-2 h-48 w-auto sm:h-60"
        >
          <img
            src="/iSource LOGO Complete.png"
            alt="iSource"
            className="h-full w-auto object-contain"
          />
        </motion.div>
      )}

      <div className="flex min-h-[500px] w-full flex-col items-center justify-center sm:min-h-[520px]">
        {renderStep === "form" && (
          <motion.div
            initial={false}
            animate={
              phase === "enter"
                ? { opacity: 1, filter: "blur(0px)", scale: 1 }
                : { opacity: 0, filter: "blur(10px)", scale: 0.98 }
            }
            transition={DISSOLVE}
            className="w-full"
            style={{ pointerEvents: phase === "leave" ? "none" : "auto" }}
          >
            <div className="flex flex-col">
              <h2 className="font-display text-center text-[2rem] leading-tight font-bold tracking-tight text-navy-950 sm:text-4xl mt-2 sm:mt-3">
                Sign in to your
                <br />
                workspace
                <span className="text-sun-500">.</span>
              </h2>

              <form ref={formRef} onSubmit={handleSubmit} noValidate className="mt-7 space-y-4">
                <div onBlur={() => setTouched((t) => ({ ...t, email: true }))}>
                  <Field
                    label="Work email"
                    type="email"
                    value={email}
                    onChange={setEmail}
                    icon={<Mail className="h-4.5 w-4.5" />}
                    autoComplete="username"
                    error={emailErr}
                    valid={EMAIL_RE.test(email)}
                    onEnterSubmit={doSubmit}
                  />
                </div>
                <div onBlur={() => setTouched((t) => ({ ...t, pw: true }))}>
                  <Field
                    label="Password"
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={setPassword}
                    icon={<Lock className="h-4.5 w-4.5" />}
                    autoComplete="current-password"
                    error={pwErr}
                    hint={
                      !pwErr && capsLock
                        ? "Caps Lock is enabled"
                        : undefined
                    }
                    onKey={detectCaps}
                    valid={password.length >= 6}
                    inputRef={pwRef}
                    onEnterSubmit={doSubmit}
                    trailing={
                      <button
                        type="button"
                        onClick={() => {
                          setShowPw((s) => !s);
                          pwRef.current?.focus();
                        }}
                        aria-label={
                          showPw ? "Hide password" : "Show password"
                        }
                        className="pointer-events-auto rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-navy-950"
                      >
                        {showPw ? (
                          <EyeOff className="h-4.5 w-4.5" />
                        ) : (
                          <Eye className="h-4.5 w-4.5" />
                        )}
                      </button>
                    }
                  />
                </div>

                <AnimatePresence initial={false}>
                  {serverError && (
                    <motion.div
                      key="server-error"
                      initial={{ opacity: 0, y: -4, height: 0 }}
                      animate={{ opacity: 1, y: 0, height: "auto" }}
                      exit={{ opacity: 0, y: -4, height: 0 }}
                      transition={{ duration: 0.3, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <div className="flex items-start gap-2 rounded-2xl border-2 border-red-400/60 bg-red-50 px-3.5 py-3 text-red-700 shadow-[0_0_0_4px_rgba(248,113,113,0.08)]">
                        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                        <div className="flex-1">
                          <p className="text-[11px] font-semibold uppercase tracking-widest text-red-600">
                            Sign in failed
                          </p>
                          <p className="mt-0.5 text-[13px] font-medium leading-snug">
                            {serverError}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex items-center pt-1">
                  <button
                    type="button"
                    onClick={() => setTrust((t) => !t)}
                    className="group flex items-center gap-2.5"
                  >
                    <span
                      className={cn(
                        "grid h-6 w-6 shrink-0 place-items-center rounded-md border-[2.5px] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.06)] transition-all duration-200",
                        trust
                          ? "border-navy-950"
                          : "border-slate-300 group-hover:border-slate-400"
                      )}
                    >
                      <AnimatePresence initial={false}>
                        {trust && (
                          <motion.span
                            key="check"
                            initial={{ opacity: 0, scale: 0.4 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.4 }}
                            transition={{ type: "spring", stiffness: 500, damping: 28 }}
                            className="grid place-items-center"
                          >
                            <Check
                              className="h-5 w-5 text-sun-500"
                              strokeWidth={4}
                              absoluteStrokeWidth
                            />
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </span>
                    <span className="text-[13px] font-medium text-slate-600 transition-colors group-hover:text-navy-950">
                      Keep me SIGNED IN
                    </span>
                  </button>
                </div>

                <motion.button
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={backendOk === false}
                  className="btn-shine group mt-2 flex w-full items-center justify-center gap-2.5 rounded-2xl bg-sun-400 py-4 font-display text-[15px] font-bold tracking-tight text-navy-950 shadow-[0_18px_36px_-14px_rgba(245,183,10,0.7)] transition-colors duration-200 hover:bg-sun-300 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-sun-400"
                >
                  {backendOk === false ? (
                    <>
                      <WifiOff className="h-4.5 w-4.5" />
                      Backend Unavailable
                    </>
                  ) : (
                    <>
                      Sign In
                      <ArrowRight className="h-4.5 w-4.5 transition-transform duration-300 group-hover:translate-x-1.5" />
                    </>
                  )}
                </motion.button>
              </form>
            </div>
          </motion.div>
        )}

        {renderStep === "authenticating" && (
          <motion.div
            initial={false}
            animate={
              phase === "enter"
                ? { opacity: 1, filter: "blur(0px)", scale: 1 }
                : { opacity: 0, filter: "blur(10px)", scale: 0.98 }
            }
            transition={DISSOLVE}
            className="w-full"
            style={{ pointerEvents: phase === "leave" ? "none" : "auto" }}
          >
            <div className="flex flex-col justify-center">
              <h2 className="font-display text-center text-2xl font-bold tracking-tight text-navy-950 sm:text-4xl">
                Authenticating
                <span className="animate-caret text-sun-500">_</span>
              </h2>
              <p className="mt-2 text-center font-mono text-[11px] tracking-[0.14em] break-all text-slate-500">
                {email}
              </p>
              <Authenticating progressStep={authProgressStep} />
            </div>
          </motion.div>
        )}

        {renderStep === "success" && (
          <motion.div
            initial={false}
            animate={
              phase === "enter"
                ? { opacity: 1, filter: "blur(0px)", scale: 1 }
                : { opacity: 0, filter: "blur(10px)", scale: 0.98 }
            }
            transition={DISSOLVE}
            className="w-full"
            style={{ pointerEvents: phase === "leave" ? "none" : "auto" }}
          >
            <div className="flex flex-col items-center justify-center text-center">
              <div className="relative mx-auto grid h-20 w-20 place-items-center">
                <span className="animate-ring-expand absolute inset-0 rounded-full border-2 border-emerald-400" />
                <motion.svg viewBox="0 0 64 64" className="h-20 w-20">
                  <motion.circle
                    cx="32"
                    cy="32"
                    r="28"
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="4"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.7, ease: EASE }}
                  />
                  <motion.path
                    d="M20 33.5 L28.5 42 L45 24"
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ delay: 0.5, duration: 0.45, ease: EASE }}
                  />
                </motion.svg>
              </div>
              <p className="mt-5 font-mono text-[10px] tracking-[0.28em] text-emerald-600">
                IDENTITY VERIFIED
              </p>
              <h2 className="font-display mt-2 text-3xl font-bold tracking-tight text-navy-950 sm:text-4xl">
                Welcome back, {welcomeName || friendlyName}
                <span className="text-sun-500">.</span>
              </h2>
              <p className="mt-3 flex items-center justify-center gap-2 text-sm text-slate-500">
                <span className="break-all font-medium text-navy-950">
                  {email}
                </span>
                <span className="rounded-md bg-navy-950 px-1.5 py-0.5 font-mono text-[9px] tracking-[0.12em] text-sun-400">
                  WC-4F2A
                </span>
              </p>

              <div className="mx-auto mt-8 max-w-sm w-full">
                <div className="flex items-center justify-between font-mono text-[10px] tracking-[0.18em] uppercase text-slate-400">
                  <span>Connecting to console</span>
                  <span className="tabular-nums">{progress}%</span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200">
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-azure-500 via-navy-900 to-sun-400"
                    animate={{ width: `${progress}%` }}
                    transition={{ ease: "linear", duration: 0.05 }}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.8 }}
        className="w-full border-t border-navy-950/10 pt-2 mt-5"
      >
        <span className="block text-center font-mono text-[10px] tracking-[0.08em] text-black">
          © 2026 iSource AppSuite INC.
        </span>
      </motion.div>
    </div>
  );
}, function areEqual(prev, next) {
  return (
    prev.onDirtyChange === next.onDirtyChange &&
    prev.onLogin === next.onLogin &&
    prev.axiosInstance === next.axiosInstance &&
    prev.backendStatus?.ok === next.backendStatus?.ok &&
    prev.backendStatus?.message === next.backendStatus?.message &&
    prev.onBackendStatusChange === next.onBackendStatusChange &&
    prev.onShowForgotPassword === next.onShowForgotPassword
  );
});
